import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import LoadingSpinner from '../../general-components/loading-spinner';
import { cleanupVideoStreamElement } from '../../../utils/video/video-streams';
import { random } from '../../../utils/random';
import useCropper from '../../../hooks/use-cropper';
import { MODAL_AVATAR_ACTIONS } from '../../../stores/reducers/modal';
import colors from '../../../utils/colors';
import { getSyncHeaders } from '../../../connection/utils';
import { GreenroomState as GS } from '../../../stores/types/types';
import { MediaItem } from '../../../stores/reducers/media';

export type ImageEditorMode =
    | 'square'          // 1:1
    | 'sixteenNine'     // 16:9
    | { aspectRatio: number; cropBox?: { left: number; top: number; width: number; height: number } };

export type ImageEditorState = 'upload' | 'edit' | 'camera';

export type ImageEditorResult = {
    file: File;
    blob: Blob;
    canvas: HTMLCanvasElement;
    source: { url: string; name: string; type: string };
    uploaded: { url: string, name: string} | null;
};

type Props = {
    onClose: () => void;
    mode: ImageEditorMode;
    allowCamera?: boolean;
    cameraStream?: MediaStream | null;
    mirrorCamera?: boolean;
    accept?: string[];
    initialImage?: { url: string; name: string; type: string };
    mediaItem?: MediaItem;
    // Labels to adapt the CTA and header (e.g., "avatar", "background", etc.)
    labels?: {
        headerUpload?: string;
        headerEdit?: string;
        headerCamera?: string;
        actionNoun?: string;
        replacePhoto?: string;
        cameraBtnUse?: string;
        cameraBtnCancel?: string;
        takePhoto?: string;
    };
    // How to output the cropped image
    output?: { mimeType: string; quality?: number };
    onConfirm: (result: ImageEditorResult) => void | Promise<void>;
    onInvalidFileType?: (file: File) => void;
    className?: string;
};

const DEFAULT_ACCEPT = ['png', 'jpg', 'jpeg'];
const DEFAULT_OUTPUT = { mimeType: 'image/png', quality: 0.92 };

const CONTAINER = { width: 560, height: 316, padding: 32 };

export default function ImageCropper({
    onClose,
    mode,
    allowCamera = true,
    cameraStream = null,
    mirrorCamera = true,
    accept = DEFAULT_ACCEPT,
    initialImage,
    mediaItem,
    labels,
    output = DEFAULT_OUTPUT,
    onConfirm,
    onInvalidFileType,
    className,
}: Props) {
    const token = useSelector((s: GS) => s.SessionReducer.token);
	const meetingId = useSelector((s: GS) => s.SessionReducer.meeting?.meeting_id);

    const [editorState, setEditorState] = useState<ImageEditorState>('upload');
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isSegmenting, setIsSegmenting] = useState(false);

    const [srcUrl, setSrcUrl] = useState<string>(initialImage?.url || '');
    const [srcName, setSrcName] = useState<string>(initialImage?.name || '');
    const [srcType, setSrcType] = useState<string>(initialImage?.type || '');
    const [originalUrl, setOriginalUrl] = useState<string>('');
    const [segmentedUrl, setSegmentedUrl] = useState<string>('');
    const [segmentedJobId, setSegmentedJobId] = useState<string>('');
    const [mediaItemId, setMediaItemId] = useState<number>(mediaItem?.id ?? 0);
    const [shouldAutoSegment, setShouldAutoSegment] = useState(false);

    const videoRef = useRef<HTMLVideoElement | null>(null);

    const aspectRatios: Record<string, number> = {
        square: 1,
        sixteenNine: 16 / 9,
    };

    const aspect = aspectRatios[mode as string] ?? mode ?? 1;

    const avatarBox = useMemo(() => {
        const { width: contW, height: contH, padding } = CONTAINER;
        return {
            left: padding + (contW - contH) / 2,
            top: padding,
            width: contH - padding * 2,
            height: contH - padding * 2,
        };
    }, []);

    const cropBoxOverride = typeof mode === 'object' && mode.cropBox ? mode.cropBox : undefined;

    const {
        imageElementRef,
        zoomRatio,
        minSliderZoom,
        maxSliderZoom,
        isLoading,
        getCroppedCanvas,
        zoomWithSlider,
    } = useCropper({
        isActive: editorState === 'edit',
        modalAvatarAction: aspect === 1 ? MODAL_AVATAR_ACTIONS.AVATAR : 'OTHER',
        originalImage: srcUrl,
        container: CONTAINER,
        avatarBox: cropBoxOverride ?? avatarBox,
    });

    // Open/close effect: reset state when opening
    useEffect(() => {
        if (initialImage?.url) {
            setSrcUrl(initialImage.url);
            setSrcName(initialImage.name);
            setSrcType(initialImage.type);
            setOriginalUrl(initialImage.url);
            setSegmentedUrl('');
            setSegmentedJobId('');
            setEditorState('edit');
        } else {
            setSrcUrl('');
            setSrcName('');
            setSrcType('');
            setOriginalUrl('');
            setSegmentedUrl('');
            setSegmentedJobId('');
            setEditorState('upload');
        }
    }, [initialImage?.url, initialImage?.name, initialImage?.type]);

    useEffect(() => {
        if (mediaItem) {

            const data = mediaItem.media_data as any;
            setOriginalUrl(data.original);
            setSegmentedUrl(data.source);
            setSegmentedJobId(data.id);
            setMediaItemId(mediaItem.id);
            setSrcName(mediaItem.original_name || mediaItem.custom_name || 'preview');
            setSrcType('image/png');

            if (data.original != srcUrl) {
                setSrcUrl(data.source);
            }

            setEditorState('edit');
        }
    }, [mediaItem]);

    // Auto-segment when image is loaded and shouldAutoSegment flag is set
    useEffect(() => {
        if (shouldAutoSegment && !isLoading && !isSegmenting && srcUrl && editorState === 'edit') {
            setShouldAutoSegment(false);
            handleSegment();
        }
    }, [shouldAutoSegment, isLoading, isSegmenting, srcUrl, editorState]);

    // Helpers
    function makeFileName(fallbackBase: string) {
        const ext = (output.mimeType.includes('/') ? output.mimeType.split('/')[1] : 'png');
        const base = srcName || fallbackBase || `image_${ random(6) }`;
        return `${ base }.${ ext }`;
    }

    function setEditorWithFile(file: File) {
        const exts = accept.map(a => a.startsWith('.') ? a.slice(1).toLowerCase() : a.toLowerCase());
        const fileExt = (file.type.split('/')[1] || '').toLowerCase();
        if (exts.length && !exts.includes(fileExt)) {
            onInvalidFileType?.(file);
            return;
        }

        if (srcUrl && srcUrl.startsWith('blob:')) {
            URL.revokeObjectURL(srcUrl);
        }

        const url = URL.createObjectURL(file);
        setSrcUrl(url);
        setSrcName(file.name.replace(/\.[^/.]+$/, ''));
        setSrcType(file.type);
        setOriginalUrl(url);
        setSegmentedUrl('');
        setSegmentedJobId('');
        setEditorState('edit');
        setShouldAutoSegment(true); // Trigger segmentation after image loads
    }

    async function handleSegment() {
        if (isSegmenting) return;

        // If we have a segmented image and currently viewing original, switch to segmented
        if (segmentedUrl && srcUrl === originalUrl) {
            setSrcUrl(segmentedUrl);
            setSrcType('image/png');
            return;
        }

        // If currently viewing segmented image, switch back to original
        if (segmentedUrl && srcUrl === segmentedUrl) {
            setSrcUrl(originalUrl);
            setSrcType('image/png');
            return;
        }

        // Otherwise, perform segmentation
        if (!srcUrl) return;

        setIsSegmenting(true);
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.crossOrigin = 'anonymous';

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = srcUrl;
            });

            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            const base64Image = canvas.toDataURL('image/png');

            const endpoint = `${process.env.REACT_APP_SYNC_HOST}/v1/removebg/generate`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: getSyncHeaders(token || ''),
                body: JSON.stringify({
                    version: 1,
                    data: [
                        { key: 'image', value: base64Image },
                        { key: 'name', value: srcName }
                    ],
                    type: '',
                    contentId: 0,
                    previousJobId: ''
                }),
            });

            if (!response.ok) {
                throw new Error('Segmentation failed');
            }

            const result = await response.json();

            if (result.status === '200' && result.data?.image && result.data?.jobId) {
                if (!originalUrl) {
                    setOriginalUrl(result.data.original);
                }
                setSegmentedUrl(result.data.image);
                setSegmentedJobId(result.data.jobId);
                setSrcUrl(result.data.image);
                setSrcType('image/png');
            }
        } catch (error) {
            console.error('Segmentation error:', error);
        } finally {
            setIsSegmenting(false);
        }
    }


    const fileInputRef = useRef<HTMLInputElement | null>(null);
    function openFilePicker() {
        fileInputRef.current?.click();
    }
    function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (e.target.files && e.target.files[0]) {
            setEditorWithFile(e.target.files[0]);
            e.target.value = '';
        }
    }

    function onDrop(e: React.DragEvent) {
        e.preventDefault();
        setIsDraggingOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) setEditorWithFile(file);
    }

    function takePhoto() {
        if (!videoRef.current) return;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;

        if (mirrorCamera) {
            ctx?.translate(canvas.width, 0);
            ctx?.scale(-1, 1);
        }
        ctx?.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/png');

        setSrcUrl(dataUrl);
        setSrcName(`camera_capture_${ random(8) }`);
        setSrcType('image/png');
        setOriginalUrl(dataUrl);
        setSegmentedUrl('');
        setSegmentedJobId('');
        setEditorState('edit');
        canvas.remove();
        setShouldAutoSegment(true); // Trigger segmentation after image loads
    }

    async function confirm() {
        const canvas = getCroppedCanvas();
        if (!canvas) return;

        setIsUploading(true);
        try {
            const blob: Blob = await new Promise((resolve) => {
                canvas.toBlob((b) => resolve(b as Blob), output.mimeType, output.quality);
            });
            const file = new File([blob], makeFileName('cropped'), { type: output.mimeType });

            if (segmentedJobId && meetingId && token) {
                try {
                    const endpoint = `${process.env.REACT_APP_API_HOST}/v2/greenroom/generated-content/removebg/media`;
                    await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Bl-token': token,
                        },
                        body: JSON.stringify({
                            imageHash: segmentedJobId,
                            mediaId: mediaItemId,
                            meetingId: meetingId,
                            original: srcUrl === originalUrl,
                        }),
                    });
                } catch (error) {
                    console.error('Error calling removebg media API:', error);
                }
            }

            let data:ImageEditorResult = {
                file,
                blob,
                canvas,
                source: { url: srcUrl, name: srcName, type: srcType },
                uploaded: null
            };

            if (segmentedJobId) {
                data.uploaded = { url: srcUrl, name: srcName };
            }

            await onConfirm(data);
        } finally {
            setIsUploading(false);
        }
    }


    const labelHeaderUpload = labels?.headerUpload ?? `Edit ${ labels?.actionNoun ?? 'avatar' }`;
    const labelHeaderEdit = labels?.headerEdit ?? `Presenter Image`;
    const labelHeaderCamera = labels?.headerCamera ?? 'Take a photo';
    const labelReplace = labels?.replacePhoto ?? 'Replace photo';
    const labelCameraUse = labels?.cameraBtnUse ?? 'Use camera';
    const labelCameraCancel = labels?.cameraBtnCancel ?? 'Cancel';
    const labelTakePhoto = labels?.takePhoto ?? 'Take photo';
    const noun = labels?.actionNoun ?? 'image';

    const headersByState: Record<ImageEditorState, string> = {
        upload: labelHeaderUpload,
        edit: labelHeaderEdit,
        camera: labelHeaderCamera,
    };
    const header = headersByState[editorState];

    return (
        <div className={classNames('info-modal image-editor', className)}>
            <div className="info-modal-header align-items-center justify-content-space-between">
                <h4 className="bl-font-header" style={{ marginBottom: 0 }}>{header}</h4>
                <button
                    style={{ padding: 8, right: -8, position: 'relative' }}
                    onClick={onClose}
                >
                    <Icon name={ICONS.CLOSE} color={COLORS.TEXT_GRAY_400} size={16} />
                </button>
            </div>

            <div className="avatar-photo-modal-body width-full">
                {editorState === 'upload' && (
                    <div
                        className={classNames('upload-container', isDraggingOver && 'dragging-over')}
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnter={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                        onDragLeave={(e) => { e.preventDefault(); setIsDraggingOver(false); }}
                        onDrop={onDrop}
                    >
                        <button className="round padding-small background-lighter" onClick={openFilePicker}>
                            <Icon name={ICONS.ADD} color={COLORS.WHITE} size={10} />
                        </button>
                        <span className="font-weight-normal bl-font-p1">Drag & Drop to add your photo</span>
                    </div>
                )}

                {editorState === 'edit' && (
                    <div style={{ position: 'relative' }}>
                        <div className="main-container">
                            <img
                                style={{ opacity: isLoading ? 0 : 1 }}
                                ref={imageElementRef}
                                src={srcUrl}
                                alt="Source"
                            />
                            <div id="crop-box-helper">
                                <Icon name={ICONS.MOVE} color={COLORS.WHITE} size={12} />
                                <span>Drag to Reposition</span>
                            </div>
                        </div>
                        {(isUploading || isSegmenting) && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: '8px',
                                pointerEvents: 'none',
                                zIndex: 100
                            }}>
                                <LoadingSpinner height={42} width={42} color={colors.paleGreen} />
                            </div>
                        )}
                    </div>
                )}

                {editorState === 'camera' && (
                    <div className="main-container">
                        <video
                            autoPlay
                            playsInline
                            style={{ transform: mirrorCamera ? 'scaleX(-1)' : 'none' }}
                            ref={(ref) => {
                                if (!ref) return;
                                if (ref && ref.srcObject !== cameraStream) {
                                    try {
                                        ref.srcObject = (cameraStream ?? null) as any;
                                        ref.volume = 0;
                                        ref.play().catch(console.error);
                                        videoRef.current = ref;
                                        cleanupVideoStreamElement(ref);
                                    } catch (e) {
                                        console.error(e);
                                    }
                                }
                            }}
                        />
                    </div>
                )}

                <div style={{ marginTop: '16px' }}>
                    <div className="crop-slider">
                        <Icon name={ICONS.ZOOM_OUT} color={COLORS.MEDIUM_GRAY} size={16} />
                        <input
                            type="range"
                            onChange={zoomWithSlider}
                            value={zoomRatio}
                            step={(maxSliderZoom - minSliderZoom) / 100 || 0.01}
                            min={minSliderZoom}
                            max={maxSliderZoom}
                            disabled={editorState !== 'edit'}
                        />
                        <Icon name={ICONS.ZOOM_IN} color={COLORS.MEDIUM_GRAY} size={16} />
                    </div>
                </div>
            </div>

            <div className="info-modal-bottom" style={{
                flexDirection: 'row'
            }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {allowCamera && (
                        <button
                            className="round background-lighter"
                            style={{ width: 48, height: 48, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => setEditorState((s) => (s === 'camera' ? 'edit' : 'camera'))}
                            disabled={!cameraStream}
                            title="Camera"
                        >
                            <Icon name={ICONS.PHOTO} color={COLORS.WHITE} size={20} />
                        </button>
                    )}

                    <button
                        className="round background-lighter"
                        style={{ width: 48, height: 48, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={openFilePicker}
                        title="Upload photo"
                    >
                        <Icon name={ICONS.UPLOAD} color={COLORS.WHITE} size={20} />
                    </button>

                    <button
                        className="round background-lighter"
                        style={{ width: 48, height: 48, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={handleSegment}
                        disabled={isSegmenting || editorState === 'upload'}
                        title={segmentedUrl ? (srcUrl === segmentedUrl ? 'Restore original' : 'Remove background') : 'Remove background'}
                    >
                        <Icon name={ICONS.BACKGROUND_RESTORE} color={COLORS.WHITE} size={20} />
                    </button>
                </div>

                <div className="info-modal-buttons">
                    <button
                        className="info-gray bl-font-l1"
                        onClick={onClose}
                    >
                        Cancel
                    </button>

                    <button
                        className="info-gradient bl-font-l1"
                        onClick={editorState === 'camera' ? takePhoto : confirm}
                        disabled={isSegmenting || isUploading || isLoading}
                    >
                        {editorState === 'camera' ? 'Take a Photo' : 'Save'}
                    </button>
                </div>
            </div>

            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept={accept.map((a) => (a.startsWith('.') ? a : `.${ a }`)).join(',')}
                onChange={onFileInputChange}
            />
        </div>
    );
}

function capitalize(s: string) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
